// Test IDs for the e2e web leg (apps/e2e). Values are identical to Android's
// test tags (TestTags.kt) and iOS's accessibility identifiers, so the three
// suites read alike and port between platforms. Add a value here only when a
// web journey needs it, and keep its string the same as the mobile one. A value
// marked "web-only" has no mobile tag yet: it follows the same
// `<screen>.<element>` style, so mobile can adopt it as it is.

/** The create-trip and edit-destinations flows' steps, as mobile names them (`WHERE`, `WHEN`, `NAME`). */
const stepName = (step: string): string => step.toUpperCase()

export const TestIds = {
	login: { screen: 'login.screen' },
	mainTabBar: { screen: 'mainTabBar.screen' },
	noTrip: { screen: 'noTrip.screen', addTripButton: 'noTrip.addTripButton' },
	trips: {
		addButton: 'trips.addButton',
		list: 'trips.list',
		card: (name: string) => `trips.card.${name}`,
	},
	tripOverview: {
		header: 'tripOverview.header',
		tripName: 'tripOverview.tripName',
		/** web-only: a section tile, keyed by its label ("Itinerary", "Ideas", …). */
		tile: (label: string) => `tripOverview.tile.${label}`,
	},
	tripCreate: {
		nameField: 'tripCreate.nameField',
		goToTripButton: 'tripCreate.goToTripButton',
		/** The flow's bottom-bar CTA, keyed by step. */
		stepCta: (step: 'where' | 'when' | 'name') => `tripCreate.cta.${stepName(step)}`,
		/** A day on the start-date calendar (create and edit flows), keyed by ISO date. */
		calendarDay: (isoDate: string) => `tripCreate.calendarDay.${isoDate}`,
		/** web-only: a destination row in the Where step, keyed by the place's name. */
		destination: (name: string) => `tripCreate.destination.${name}`,
	},
	tripDestinations: {
		/** The edit-destinations flow's bottom-bar CTA, keyed by step: Next on Where, Save on When. */
		stepCta: (step: 'where' | 'when') => `tripDestinations.cta.${stepName(step)}`,
	},
	location: {
		searchField: 'location.searchField',
		/** The "use it as typed" row that turns free text into a place without Google. */
		useCustomButton: 'location.useCustomButton',
	},
	me: {
		nameLabel: 'me.nameLabel',
		editProfileButton: 'me.editProfileButton',
		firstNameField: 'me.firstNameField',
		lastNameField: 'me.lastNameField',
		saveButton: 'me.saveButton',
	},
	itinerary: {
		getStartedButton: 'itinerary.getStartedButton',
		addEventButton: 'itinerary.addEventButton',
		eventCell: (name: string) => `itinerary.eventCell.${name}`,
		/** A trip day on the calendar, keyed by its zero-based day index. */
		calendarDay: (dayIndex: number) => `itinerary.calendarDay.${dayIndex}`,
		/** The create-event flow's Title field, on its What step. */
		eventNameField: 'itinerary.eventNameField',
		/** The create-event flow's "Create Event", on its last step. */
		eventSubmitButton: 'itinerary.eventSubmitButton',
		/** The create-event flow's "Next", on every step before the last. */
		eventNextButton: 'itinerary.eventNextButton',
		/** web-only: the destinations legend under the calendar. */
		destinationLegend: 'itinerary.destinationLegend',
		/** web-only: the selected day's "Import idea" button. */
		importIdeaButton: 'itinerary.importIdeaButton',
		/** web-only: "Next" in the import-idea dialog, which opens the create-event flow filled from the idea. */
		importIdeaNextButton: 'itinerary.importIdeaNextButton',
		/** web-only: the page's "AI import" button. */
		aiImportButton: 'itinerary.aiImportButton',
	},
	aiImport: {
		notesField: 'aiImport.notesField',
		ctaButton: 'aiImport.ctaButton',
		/** An event on the import's result list, keyed by the event name. */
		reviewEvent: (name: string) => `aiImport.reviewEvent.${name}`,
		/** web-only: "View on timeline" once the import has added its events. */
		viewTimelineButton: 'aiImport.viewTimelineButton',
	},
	idea: {
		nameField: 'idea.nameField',
		submitButton: 'idea.submitButton',
		/** The idea's name on its detail page. */
		detailsName: 'idea.detailsName',
		editButton: 'idea.editButton',
		deleteButton: 'idea.deleteButton',
		/** An idea card (ideas grid + import-idea picker), keyed by the idea name. */
		cell: (name: string) => `idea.cell.${name}`,
		/** web-only: the idea picker's search field. */
		selectField: 'idea.selectField',
		/** web-only: the form's "Add Link" button. */
		addLinkButton: 'idea.addLinkButton',
		/** web-only: a link row's text field on the form. */
		linkField: 'idea.linkField',
	},
	expense: {
		nameField: 'expense.nameField',
		amountField: 'expense.amountField',
		confirmButton: 'expense.confirmButton',
		/** A card in the activity list, keyed by the expense name. */
		cell: (name: string) => `expense.cell.${name}`,
	},
	payUp: {
		recordScreen: 'payUp.recordScreen',
		confirmButton: 'payUp.confirmButton',
		timelineCell: 'payUp.timelineCell',
		/** A balance with another member ("Record Pay Up"), keyed by that member's display name. */
		balanceCell: (name: string) => `payUp.balanceCell.${name}`,
		/** web-only: "Delete" on a pay up's details. */
		deleteButton: 'payUp.deleteButton',
	},
	/** web-only: mobile has no flight tags yet. */
	flight: {
		codeField: 'flight.codeField',
		dateField: 'flight.dateField',
		/** "Add Flight" / "Add Leg": looks the leg up and adds it. */
		addLegButton: 'flight.addLegButton',
		doneButton: 'flight.doneButton',
		/** A flight card on the Flights page, keyed by its flight code. */
		cell: (code: string) => `flight.cell.${code}`,
		editButton: 'flight.editButton',
		deleteButton: 'flight.deleteButton',
		notesField: 'flight.notesField',
		saveButton: 'flight.saveButton',
		notes: 'flight.notes',
	},
	/** web-only: mobile has no accommodation tags yet. */
	accommodation: {
		nameField: 'accommodation.nameField',
		checkInField: 'accommodation.checkInField',
		checkOutField: 'accommodation.checkOutField',
		saveButton: 'accommodation.saveButton',
		/** A card on the Accommodations page, keyed by its name. */
		cell: (name: string) => `accommodation.cell.${name}`,
		detailsName: 'accommodation.detailsName',
		editButton: 'accommodation.editButton',
		deleteButton: 'accommodation.deleteButton',
	},
	common: {
		/** Confirm on a confirmation dialog. */
		popupConfirm: 'common.popupConfirm',
		/** A page's primary add action (Ideas "Add Idea", Expenses "Add Expense", …) — mobile's FAB. */
		fab: 'common.fab',
	},
} as const
